import PropTypes from 'prop-types';
import { useMemo, useState } from 'react';
import {
  Box,
  Card,
  Table,
  TableBody,
  TableCell,
  TableRow,
  Typography,
  IconButton,
  Tooltip,
  useTheme
} from '@mui/material';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useSelector } from 'react-redux';
import { getFormattedDate, getGroupName } from 'src/utils/getInitials';
import { GROUP_TYPES } from 'src/utils/constants';
import EnhancedTableHead, { getComparator } from 'src/common/EnhancedTableHead';
import { getAlignByColumnId, groupTableHeaders } from 'src/utils/tableHeaders';
import CustomPagination from 'src/common/CustomPagination';
import {
  getTableActionIconButtonSx,
  TABLE_ACTION_ICON_SX
} from 'src/common/tableActionStyles';

const GroupList = ({ groups = [], onEditGroup }) => {
  const theme = useTheme();
  const { groupType } = useSelector((state) => state.groups);

  const [limit, setLimit] = useState(10);
  const [page, setPage] = useState(0);
  const [order, setOrder] = useState('desc');
  const [orderBy, setOrderBy] = useState('id');
  const groupAlignById = useMemo(() => getAlignByColumnId(groupTableHeaders), []);

  const handleRequestSort = (event, property) => {
    const isAsc = orderBy === property && order === 'asc';
    setOrder(isAsc ? 'desc' : 'asc');
    setOrderBy(property);
  };

  const handleLimitChange = (event) => {
    setLimit(parseInt(event.target.value, 10));
    setPage(0);
  };

  const handlePageChange = (event, newPage) => {
    setPage(newPage);
  };

  const displayData = groups
    .slice()
    .sort(getComparator(order, orderBy))
    .slice(page * limit, page * limit + limit);

  return (
    <Card
      sx={{
        borderRadius: 2,
        boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}
    >
      <Box
        sx={{
          minHeight: 0,
          flex: 1,
          overflow: 'auto',
          '& .MuiTableCell-stickyHeader': {
            backgroundColor: '#F9FAFB',
            zIndex: 2
          },
          '& .MuiTableHead-root .MuiTableCell-root:last-of-type': {
            position: 'sticky',
            right: 0,
            zIndex: 3,
            backgroundColor: '#F9FAFB',
            boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
          }
        }}
      >
        <Box sx={{ minWidth: { xs: 680, sm: 800, md: 900 } }}>
          <Table stickyHeader>
            <EnhancedTableHead
              order={order}
              orderBy={orderBy}
              headCells={groupTableHeaders}
              onRequestSort={handleRequestSort}
            />

            <TableBody>
              {displayData
                .map((group) => (
                  <TableRow
                    hover
                    key={group.id}
                    sx={{
                      '&:hover': { backgroundColor: '#F9FAFB' }
                    }}
                  >
                    <TableCell align={groupAlignById.id}>
                      <Typography fontWeight={600}>
                        {group.id}
                      </Typography>
                    </TableCell>

                    <TableCell align={groupAlignById.name}>{group.name}</TableCell>

                    <TableCell align={groupAlignById.type}>
                      {getGroupName(groupType, GROUP_TYPES)}
                    </TableCell>

                    <TableCell align={groupAlignById.created_at}>
                      {getFormattedDate(group.created_at, 'DD/MM/YYYY')}
                    </TableCell>

                    <TableCell
                      align={groupAlignById.action}
                      sx={{
                        position: 'sticky',
                        right: 0,
                        zIndex: 1,
                        backgroundColor: '#FFFFFF',
                        boxShadow: '-1px 0 0 rgba(0, 0, 0, 0.08)'
                      }}
                    >
                      <Tooltip title="Edit Group">
                        <IconButton
                          size="medium"
                          aria-label="Edit group"
                          sx={getTableActionIconButtonSx(theme)}
                          onClick={(e) => {
                            e.stopPropagation();
                            onEditGroup(group);
                          }}
                        >
                          <ChevronRightIcon sx={TABLE_ACTION_ICON_SX} />
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </Box>
      </Box>

      <Box sx={{ flexShrink: 0, bgcolor: '#FFFFFF' }}>
        <CustomPagination
          page={page}
          rowsPerPage={limit}
          count={groups.length}
          onPageChange={handlePageChange}
          onRowsPerPageChange={handleLimitChange}
        />
      </Box>
    </Card>
  );
};

GroupList.propTypes = {
  groups: PropTypes.array.isRequired,
  onEditGroup: PropTypes.func.isRequired
};

export default GroupList;
